// =============================================================================
// SURVEYJS ADAPTER - Convert between custom form JSON and SurveyJS format
// =============================================================================

import type {
  Form,
  FormField,
  FormSection,
  FormSettings,
  FieldType,
  FieldOption,
} from '../types';

// SurveyJS types (simplified for our usage)
interface SurveyJSQuestion {
  type: string;
  name: string;
  title?: string | undefined;
  description?: string | undefined;
  placeholder?: string | undefined;
  isRequired?: boolean | undefined;
  defaultValue?: unknown;
  choices?: Array<{ value: string; text: string }> | undefined;
  inputType?: string | undefined;
  rows?: number | undefined;
  showNoneItem?: boolean | undefined;
  showOtherItem?: boolean | undefined;
  rateMax?: number | undefined;
  acceptedTypes?: string | undefined;
  visibleIf?: string | undefined;
  validators?: Array<{
    type: string;
    text?: string | undefined;
    minLength?: number | undefined;
    maxLength?: number | undefined;
    minValue?: number | undefined;
    maxValue?: number | undefined;
    regex?: string | undefined;
  }> | undefined;
  html?: string | undefined;
}

interface SurveyJSPage {
  name: string;
  title?: string | undefined;
  description?: string | undefined;
  elements: SurveyJSQuestion[];
}

interface SurveyJSModel {
  title?: string | undefined;
  description?: string | undefined;
  pages: SurveyJSPage[];
  showProgressBar?: string | undefined;
  showQuestionNumbers?: string | undefined;
  completeText?: string | undefined;
  completedHtml?: string | undefined;
}

// =============================================================================
// FIELD TYPE MAPPING
// =============================================================================

const FIELD_TO_SURVEYJS: Record<FieldType, { type: string; inputType?: string }> = {
  text: { type: 'text' },
  email: { type: 'text', inputType: 'email' },
  number: { type: 'text', inputType: 'number' },
  phone: { type: 'text', inputType: 'tel' },
  textarea: { type: 'comment' },
  select: { type: 'dropdown' },
  multiselect: { type: 'checkbox' },
  checkbox: { type: 'boolean' },
  radio: { type: 'radiogroup' },
  date: { type: 'text', inputType: 'date' },
  datetime: { type: 'text', inputType: 'datetime-local' },
  file: { type: 'file' },
  image: { type: 'file' },
  signature: { type: 'signaturepad' },
  rating: { type: 'rating' },
  section: { type: 'panel' },
  divider: { type: 'html' },
};

const SURVEYJS_TO_FIELD: Record<string, FieldType> = {
  text: 'text',
  comment: 'textarea',
  dropdown: 'select',
  checkbox: 'multiselect',
  boolean: 'checkbox',
  radiogroup: 'radio',
  file: 'file',
  signaturepad: 'signature',
  rating: 'rating',
  panel: 'section',
  html: 'divider',
};

// =============================================================================
// CONVERT FROM CUSTOM FORMAT TO SURVEYJS
// =============================================================================

function convertFieldToSurveyJS(field: FormField): SurveyJSQuestion {
  const mapping = FIELD_TO_SURVEYJS[field.type];

  const question: SurveyJSQuestion = {
    type: mapping.type,
    name: field.name,
    title: field.label,
    description: field.helpText,
    placeholder: field.placeholder,
    isRequired: field.validation.some((v) => v.type === 'required'),
    defaultValue: field.defaultValue,
  };

  // Add input type for text inputs
  if (mapping.inputType) {
    question.inputType = mapping.inputType;
  }

  // Convert options for select/multiselect/radio
  if (field.options && field.options.length > 0) {
    question.choices = field.options.map((opt) => ({
      value: opt.value,
      text: opt.label,
    }));
  }

  // Convert validators
  const validators: SurveyJSQuestion['validators'] = [];
  for (const rule of field.validation) {
    switch (rule.type) {
      case 'minLength':
        validators.push({
          type: 'text',
          text: rule.message,
          minLength: rule.value as number,
        });
        break;
      case 'maxLength':
        validators.push({
          type: 'text',
          text: rule.message,
          maxLength: rule.value as number,
        });
        break;
      case 'min':
        validators.push({
          type: 'numeric',
          text: rule.message,
          minValue: rule.value as number,
        });
        break;
      case 'max':
        validators.push({
          type: 'numeric',
          text: rule.message,
          maxValue: rule.value as number,
        });
        break;
      case 'pattern':
        validators.push({
          type: 'regex',
          text: rule.message,
          regex: rule.value as string,
        });
        break;
      case 'email':
        validators.push({
          type: 'email',
          text: rule.message,
        });
        break;
      case 'url':
        validators.push({
          type: 'regex',
          text: rule.message,
          regex: '^https?://.+',
        });
        break;
    }
  }
  if (validators.length > 0) {
    question.validators = validators;
  }

  // Convert conditional visibility
  if (field.conditional) {
    const { fieldId, operator, value } = field.conditional;
    let condition = '';
    switch (operator) {
      case 'equals':
        condition = `{${fieldId}} = '${value}'`;
        break;
      case 'notEquals':
        condition = `{${fieldId}} <> '${value}'`;
        break;
      case 'contains':
        condition = `{${fieldId}} contains '${value}'`;
        break;
      case 'isEmpty':
        condition = `{${fieldId}} empty`;
        break;
      case 'isNotEmpty':
        condition = `{${fieldId}} notempty`;
        break;
    }
    if (condition) {
      question.visibleIf = condition;
    }
  }

  // Handle divider as HTML
  if (field.type === 'divider') {
    question.html = '<hr class="my-4 border-secondary-200" />';
  }

  return question;
}

function convertSectionToSurveyJS(section: FormSection): SurveyJSPage {
  return {
    name: section.id,
    title: section.title,
    description: section.description,
    elements: section.fields.map(convertFieldToSurveyJS),
  };
}

export function convertFormToSurveyJS(form: Form): SurveyJSModel {
  return {
    title: form.name,
    description: form.description || undefined,
    pages: form.sections.map(convertSectionToSurveyJS),
    showProgressBar: form.settings.showProgressBar ? 'top' : 'off',
    showQuestionNumbers: 'off',
    completeText: form.settings.submitLabel,
    completedHtml: `<div class="text-center p-8"><h3 class="text-xl font-semibold text-green-600">${form.settings.successMessage}</h3></div>`,
  };
}

// =============================================================================
// CONVERT FROM SURVEYJS TO CUSTOM FORMAT
// =============================================================================

function determineFieldType(question: SurveyJSQuestion): FieldType {
  // Check input type first for text fields
  if (question.type === 'text' && question.inputType) {
    switch (question.inputType) {
      case 'email':
        return 'email';
      case 'number':
        return 'number';
      case 'tel':
        return 'phone';
      case 'date':
        return 'date';
      case 'datetime-local':
        return 'datetime';
    }
  }

  return SURVEYJS_TO_FIELD[question.type] || 'text';
}

function convertSurveyJSToField(question: SurveyJSQuestion): FormField {
  const fieldType = determineFieldType(question);

  const field: FormField = {
    id: question.name,
    type: fieldType,
    name: question.name,
    label: question.title || question.name,
    validation: [],
    width: 'full',
  };

  // Only set optional properties if they have values
  if (question.placeholder) field.placeholder = question.placeholder;
  if (question.description) field.helpText = question.description;
  if (question.defaultValue !== undefined) field.defaultValue = question.defaultValue;

  // Convert choices to options
  if (question.choices && question.choices.length > 0) {
    field.options = question.choices.map((choice): FieldOption => ({
      label: typeof choice === 'string' ? choice : choice.text,
      value: typeof choice === 'string' ? choice : choice.value,
    }));
  }

  // Convert required
  if (question.isRequired) {
    field.validation.push({
      type: 'required',
      message: 'This field is required',
    });
  }

  // Convert validators
  if (question.validators) {
    for (const validator of question.validators) {
      switch (validator.type) {
        case 'text':
          if (validator.minLength) {
            field.validation.push({
              type: 'minLength',
              value: validator.minLength,
              message: validator.text || `Minimum ${validator.minLength} characters`,
            });
          }
          if (validator.maxLength) {
            field.validation.push({
              type: 'maxLength',
              value: validator.maxLength,
              message: validator.text || `Maximum ${validator.maxLength} characters`,
            });
          }
          break;
        case 'numeric':
          if (validator.minValue !== undefined) {
            field.validation.push({
              type: 'min',
              value: validator.minValue,
              message: validator.text || `Minimum value ${validator.minValue}`,
            });
          }
          if (validator.maxValue !== undefined) {
            field.validation.push({
              type: 'max',
              value: validator.maxValue,
              message: validator.text || `Maximum value ${validator.maxValue}`,
            });
          }
          break;
        case 'email':
          field.validation.push({
            type: 'email',
            message: validator.text || 'Please enter a valid email',
          });
          break;
        case 'regex':
          if (validator.regex) {
            field.validation.push({
              type: 'pattern',
              value: validator.regex,
              message: validator.text || 'Invalid format',
            });
          }
          break;
      }
    }
  }

  // Convert conditional visibility
  if (question.visibleIf) {
    const parsed = parseVisibleIf(question.visibleIf);
    if (parsed) {
      field.conditional = parsed;
    }
  }

  return field;
}

function parseVisibleIf(condition: string): FormField['conditional'] | null {
  // Parse SurveyJS condition expressions
  // Examples: "{field} = 'value'", "{field} <> 'value'", "{field} empty"

  const equalsMatch = condition.match(/\{(\w+)\}\s*=\s*'([^']*)'/);
  if (equalsMatch && equalsMatch[1]) {
    return {
      fieldId: equalsMatch[1],
      operator: 'equals',
      value: equalsMatch[2],
    };
  }

  const notEqualsMatch = condition.match(/\{(\w+)\}\s*<>\s*'([^']*)'/);
  if (notEqualsMatch && notEqualsMatch[1]) {
    return {
      fieldId: notEqualsMatch[1],
      operator: 'notEquals',
      value: notEqualsMatch[2],
    };
  }

  const containsMatch = condition.match(/\{(\w+)\}\s*contains\s*'([^']*)'/);
  if (containsMatch && containsMatch[1]) {
    return {
      fieldId: containsMatch[1],
      operator: 'contains',
      value: containsMatch[2],
    };
  }

  const emptyMatch = condition.match(/\{(\w+)\}\s*empty/);
  if (emptyMatch && emptyMatch[1]) {
    return {
      fieldId: emptyMatch[1],
      operator: 'isEmpty',
    };
  }

  const notEmptyMatch = condition.match(/\{(\w+)\}\s*notempty/);
  if (notEmptyMatch && notEmptyMatch[1]) {
    return {
      fieldId: notEmptyMatch[1],
      operator: 'isNotEmpty',
    };
  }

  return null;
}

function convertSurveyJSToSection(page: SurveyJSPage): FormSection {
  const section: FormSection = {
    id: page.name,
    fields: page.elements.map(convertSurveyJSToField),
  };
  if (page.title) section.title = page.title;
  if (page.description) section.description = page.description;
  return section;
}

export function convertSurveyJSToForm(
  surveyModel: SurveyJSModel,
  existingForm?: Partial<Form>
): Form {
  const now = new Date().toISOString();

  // Parse settings from survey model
  const settings: FormSettings = {
    submitLabel: surveyModel.completeText || 'Submit',
    successMessage: extractSuccessMessage(surveyModel.completedHtml),
    allowMultipleSubmissions: false,
    showProgressBar: surveyModel.showProgressBar !== 'off',
    theme: 'default',
  };

  return {
    id: existingForm?.id || crypto.randomUUID(),
    name: surveyModel.title || existingForm?.name || 'Untitled Form',
    description: surveyModel.description || existingForm?.description,
    sections: surveyModel.pages.map(convertSurveyJSToSection),
    settings,
    createdAt: existingForm?.createdAt || now,
    updatedAt: now,
  };
}

function extractSuccessMessage(completedHtml?: string): string {
  if (!completedHtml) return 'Thank you for your submission!';

  // Try to extract text from HTML
  const match = completedHtml.match(/<h3[^>]*>([^<]+)<\/h3>/);
  return match && match[1] ? match[1] : 'Thank you for your submission!';
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getSurveyJSQuestionType(fieldType: FieldType): string {
  return FIELD_TO_SURVEYJS[fieldType].type;
}

export function getFieldType(surveyJSType: string, inputType?: string): FieldType {
  if (surveyJSType === 'text' && inputType) {
    const inputTypeMap: Record<string, FieldType> = {
      email: 'email',
      number: 'number',
      tel: 'phone',
      date: 'date',
      'datetime-local': 'datetime',
    };
    if (inputTypeMap[inputType]) {
      return inputTypeMap[inputType];
    }
  }
  return SURVEYJS_TO_FIELD[surveyJSType] || 'text';
}
